import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

// Performance Chart Component
export const PerformanceChart: React.FC<ChartProps> = ({ metrics, chartId = 'default' }) => {
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getPerformanceColors = (brandValue: number, industryMean: number) => {
    const absoluteDifference = calculateAbsoluteDifference(brandValue, industryMean);
    const perfLevel = getPerformanceLevel(absoluteDifference);

    const colorMaps = {
      'excellent': {
        brandGradient: ['#dcfce7', '#16a34a'],
        industryColor: '#94a3b8',
        borderColor: '#16a34a',
        bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
        border: '#16a34a',
        accent: '#15803d',
        text: '#14532d',
        badgeColor: '#15803d',
        badgeBg: '#dcfce7',
        performance: 'excellent' as const
      },
      'very-good': {
        brandGradient: ['#ecfccb', '#22c55e'],
        industryColor: '#94a3b8',
        borderColor: '#22c55e',
        bg: 'linear-gradient(135deg, #ecfccb 0%, #bbf7d0 100%)',
        border: '#22c55e',
        accent: '#16a34a',
        text: '#15803d',
        badgeColor: '#16a34a',
        badgeBg: '#ecfccb',
        performance: 'very-good' as const
      },
      'good': {
        brandGradient: ['#f0fdf4', '#4ade80'],
        industryColor: '#94a3b8',
        borderColor: '#4ade80',
        bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        border: '#4ade80',
        accent: '#22c55e',
        text: '#16a34a',
        badgeColor: '#22c55e',
        badgeBg: '#f0fdf4',
        performance: 'good' as const
      },
      'fair': {
        brandGradient: ['#fef3c7', '#f59e0b'],
        industryColor: '#94a3b8',
        borderColor: '#f59e0b',
        bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        border: '#f59e0b',
        accent: '#d97706',
        text: '#92400e',
        badgeColor: '#d97706',
        badgeBg: '#fef3c7',
        performance: 'fair' as const
      },
      'poor': {
        brandGradient: ['#fef2f2', '#ef4444'],
        industryColor: '#94a3b8',
        borderColor: '#ef4444',
        bg: 'linear-gradient(135deg, #fef2f2 0%, #fca5a5 100%)',
        border: '#ef4444',
        accent: '#dc2626',
        text: '#b91c1c',
        badgeColor: '#dc2626',
        badgeBg: '#fef2f2',
        performance: 'poor' as const
      },
      'critical': {
        brandGradient: ['#fee2e2', '#dc2626'],
        industryColor: '#94a3b8',
        borderColor: '#dc2626',
        bg: 'linear-gradient(135deg, #fee2e2 0%, #f87171 100%)',
        border: '#dc2626',
        accent: '#b91c1c',
        text: '#991b1b',
        badgeColor: '#b91c1c',
        badgeBg: '#fee2e2',
        performance: 'very-poor' as const
      }
    };

    return colorMaps[perfLevel.performance];
  };

  useEffect(() => {
    if (!metrics || metrics.length === 0 || !chartRef.current || !containerRef.current) return;

    const drawChart = () => {
      // Clear previous chart
      d3.select(chartRef.current).selectAll('*').remove();

      // Get container width for responsive sizing
      const containerWidth = containerRef.current?.offsetWidth || 1200;

      const margin = { top: 40, right: 50, bottom: 120, left: 80 };
      const width = Math.max(containerWidth - margin.left - margin.right, 500);
      const height = 520 - margin.top - margin.bottom;

      const svg = d3.select(chartRef.current)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom);

      // Add gradient definitions for each bar
      const defs = svg.append('defs');

      metrics.forEach((metric, i) => {
        const colors = getPerformanceColors(metric.value, metric.industryMean || 0);

        const gradient = defs.append('linearGradient')
          .attr('id', `brandGradient${i}`)
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '0%')
          .attr('y2', '100%');

        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', colors.brandGradient[0]);

        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', colors.brandGradient[1]);

        // Shadow filter
        const filter = defs.append('filter')
          .attr('id', `shadow${i}`)
          .attr('x', '-50%')
          .attr('y', '-50%')
          .attr('width', '200%')
          .attr('height', '200%');

        filter.append('feDropShadow')
          .attr('dx', '2')
          .attr('dy', '4')
          .attr('stdDeviation', '3')
          .attr('flood-opacity', '0.2');
      });

      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Add background with subtle gradient
      const bgGradient = defs.append('linearGradient')
        .attr('id', 'backgroundGradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      bgGradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', '#fafafa');

      bgGradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', '#f1f5f9');

      g.append('rect')
        .attr('width', width)
        .attr('height', height)
        .attr('fill', 'url(#backgroundGradient)')
        .attr('rx', 12)
        .attr('stroke', 'rgba(0,0,0,0.05)')
        .attr('stroke-width', 1);

      // Scales
      const x = d3.scaleBand()
        .domain(metrics.map(d => d.categoryName))
        .range([20, width - 20])
        .padding(0.3);

      // Calculate proper domain for brand equity scores
      const allValues = [...metrics.map(d => d.value), ...metrics.map(d => d.industryMean || 0)];
      const minValue = Math.min(...allValues);
      const maxValue = Math.max(...allValues);
      const domainPadding = (maxValue - minValue) * 0.15;

      const y = d3.scaleLinear()
        .domain([Math.min(0, minValue - domainPadding), maxValue + domainPadding])
        .range([height - 20, 40]);

      // Create or select existing tooltip
      const tooltip = d3.select('body').selectAll(`.chart-tooltip-${chartId}`)
        .data([null])
        .join('div')
        .attr('class', `chart-tooltip chart-tooltip-${chartId}`)
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('z-index', '1000')
        .style('pointer-events', 'none');

      // Brand performance bars with gradients and enhanced styling
      g.selectAll('.brand-bar')
        .data(metrics)
        .enter().append('rect')
        .attr('class', 'brand-bar')
        .attr('x', d => (x(d.categoryName) || 0) + 2)
        .attr('width', x.bandwidth() / 2 - 4)
        .attr('y', d => y(d.value))
        .attr('height', d => Math.max(0, height - 20 - y(d.value)))
        .attr('fill', (d, i) => `url(#brandGradient${i})`)
        .attr('filter', (d, i) => `url(#shadow${i})`)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('stroke', (d) => {
          const colors = getPerformanceColors(d.value, d.industryMean || 0);
          return colors.borderColor;
        })
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          const hasIndustryData = d.industryMean && d.industryMean > 0;
          const colors = getPerformanceColors(d.value, d.industryMean || 0);
          const absoluteDifference = hasIndustryData ? calculateAbsoluteDifference(d.value, d.industryMean || 0) : 0;

          const performanceText = !hasIndustryData ? 'Current Score' :
                                 colors.performance === 'excellent' ? 'Outstanding Performance' :
                                 colors.performance === 'very-good' ? 'Strong Performance' :
                                 colors.performance === 'good' ? 'Above Average' :
                                 colors.performance === 'fair' ? 'Below Average' :
                                 colors.performance === 'poor' ? 'Needs Improvement' : 'Urgent Attention Required';

          tooltip.html(`
            <div style="
              background: ${colors.bg};
              border: 2px solid ${colors.border};
              border-radius: 12px;
              padding: 16px;
              min-width: 200px;
              box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.25);
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            ">
              <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;
              ">
                ${hasIndustryData ? `
                <div style="
                  background: ${colors.badgeBg};
                  border: 2px solid ${colors.badgeColor};
                  border-radius: 16px;
                  padding: 6px 10px;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                ">
                  <span style="
                    color: ${colors.badgeColor};
                    font-size: 12px;
                    font-weight: 700;
                  ">
                    ${absoluteDifference > 0 ? '↗' : '↘'} ${absoluteDifference > 0 ? '+' : ''}${absoluteDifference.toFixed(1)}%
                  </span>
                </div>
                ` : `
                <div style="
                  background: #f3f4f6;
                  border: 2px solid #d1d5db;
                  border-radius: 16px;
                  padding: 6px 10px;
                ">
                  <span style="
                    color: #6b7280;
                    font-size: 11px;
                    font-weight: 600;
                  ">
                    No Benchmark
                  </span>
                </div>
                `}
              </div>

              <div style="
                font-size: 13px;
                font-weight: 600;
                color: #64748b;
                margin-bottom: 8px;
              ">
                ${d.categoryName}
              </div>

              <div style="
                font-size: 28px;
                font-weight: 800;
                color: ${colors.text};
                line-height: 1;
                margin-bottom: 6px;
              ">
                ${d.value.toFixed(1)}%
              </div>

              <div style="
                font-size: 11px;
                color: ${colors.text};
                font-weight: 500;
                opacity: 0.8;
                margin-bottom: 12px;
              ">
                ${performanceText}
              </div>

              ${hasIndustryData ? `
              <div style="
                border-top: 1px solid rgba(0,0,0,0.1);
                padding-top: 10px;
                display: flex;
                justify-content: space-between;
                align-items: center;
              ">
                <span style="color: #64748b; font-size: 11px;">
                  Industry Avg
                </span>
                <span style="
                  color: #64748b;
                  font-size: 13px;
                  font-weight: 600;
                ">
                  ${(d.industryMean || 0).toFixed(1)}%
                </span>
              </div>
              ` : `
              <div style="
                border-top: 1px solid rgba(0,0,0,0.1);
                padding-top: 10px;
                text-align: center;
              ">
                <span style="color: #94a3b8; font-size: 11px; font-style: italic;">
                  No benchmark data available yet
                </span>
              </div>
              `}
            </div>
          `)
          .style('visibility', 'visible')
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');

          // Highlight the bar
          d3.select(this)
            .attr('stroke-width', 3)
            .style('filter', 'brightness(1.1)');
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');

          // Reset bar appearance
          d3.select(this)
            .attr('stroke-width', 2)
            .style('filter', (d, i) => `url(#shadow${i})`);
        });

      // Industry average bars with subtle styling (only show if industryMean exists)
      g.selectAll('.industry-bar')
        .data(metrics.filter(d => d.industryMean && d.industryMean > 0))
        .enter().append('rect')
        .attr('class', 'industry-bar')
        .attr('x', d => (x(d.categoryName) || 0) + x.bandwidth() / 2 + 2)
        .attr('width', x.bandwidth() / 2 - 4)
        .attr('y', d => y(d.industryMean || 0))
        .attr('height', d => Math.max(0, height - 20 - y(d.industryMean || 0)))
        .attr('fill', '#cbd5e1')
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('stroke', '#94a3b8')
        .attr('stroke-width', 1)
        .attr('opacity', 0.9);

      // Enhanced grid lines
      const yTicks = y.ticks(8);
      g.selectAll('.grid-line')
        .data(yTicks)
        .enter().append('line')
        .attr('class', 'grid-line')
        .attr('x1', 20)
        .attr('x2', width - 20)
        .attr('y1', d => y(d))
        .attr('y2', d => y(d))
        .attr('stroke', 'rgba(148, 163, 184, 0.3)')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3');

      // X Axis with enhanced styling
      g.append('g')
        .attr('transform', `translate(0,${height - 20})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .style('text-anchor', 'end')
        .style('font-size', '12px')
        .style('font-weight', '500')
        .style('fill', '#64748b')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-35)')
        .text((d: any) => d as string);

      // Y Axis with enhanced styling
      g.append('g')
        .attr('transform', `translate(20,0)`)
        .call(d3.axisLeft(y).ticks(8))
        .selectAll('text')
        .style('font-size', '12px')
        .style('font-weight', '500')
        .style('fill', '#64748b');

      // Style the axis lines
      g.selectAll('.domain')
        .style('stroke', '#cbd5e1')
        .style('stroke-width', 2);

      g.selectAll('.tick line')
        .style('stroke', '#cbd5e1')
        .style('stroke-width', 1);

      // Enhanced Legend with card-like styling
      const legend = g.append('g')
        .attr('transform', `translate(${width - 220}, 15)`);

      // Legend background
      legend.append('rect')
        .attr('x', -15)
        .attr('y', -10)
        .attr('width', 210)
        .attr('height', 65)
        .attr('fill', 'white')
        .attr('rx', 8)
        .attr('stroke', 'rgba(0,0,0,0.1)')
        .attr('stroke-width', 1)
        .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))');

      // Your Brand legend
      legend.append('rect')
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', 'url(#brandGradient0)')
        .attr('rx', 3)
        .attr('stroke', '#16a34a')
        .attr('stroke-width', 1);

      legend.append('text')
        .attr('x', 24)
        .attr('y', 13)
        .text('Your Brand')
        .style('font-size', '13px')
        .style('font-weight', '600')
        .style('fill', '#374151');

      // Industry Average legend
      legend.append('rect')
        .attr('y', 28)
        .attr('width', 16)
        .attr('height', 16)
        .attr('fill', '#e2e8f0')
        .attr('rx', 3)
        .attr('stroke', '#cbd5e1')
        .attr('stroke-width', 1);

      legend.append('text')
        .attr('x', 24)
        .attr('y', 41)
        .text('Industry Average')
        .style('font-size', '13px')
        .style('font-weight', '600')
        .style('fill', '#374151');

    };

    // Draw initial chart
    drawChart();

    // Add resize listener
    const handleResize = () => {
      drawChart();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      // Clean up tooltip
      d3.select('body').select(`.chart-tooltip-${chartId}`).remove();
    };
  }, [metrics, chartId]);

  return (
    <div ref={containerRef} style={{ width: '100%', minHeight: '540px' }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};